'use client';

import ChevronRight from '@/components/svg/ChevronRightV2';
import ChevronRightMobile from '@/components/svg/ChevronRightV3';
import Button from '@/components/V2/Button';
import { useWindowDimensions } from '@/helpers/hooks';
import { useGetRecentOrdersQuery } from '@/store/api/dashboard';

import ReportsLoader from '../Reports/CustomerDetailModal/ReportsLoader';

const primaryColor = 'v2-bg-primary';

const statusColors: Record<string, string> = {
  Shipped: 'v2-bg-[#3B2122]',
  Printed: 'v2-bg-[#E08D6E]',
  Accepted: primaryColor,
  Processing: 'v2-bg-[#154734]',
  Cancelled: 'v2-bg-[#9CA3AF]',
  Closed: 'v2-bg-[#3B2122]',
  Pending: 'v2-bg-[#E08D6E]',
};

const RecentOrders = () => {
  const { data, isLoading, isError } = useGetRecentOrdersQuery();
  const { width } = useWindowDimensions();
  const isDesktop = width > 1534;

  const handleOrderClick = (orderToken: string) => {
    window.location.href = `/order?token=${encodeURIComponent(orderToken)}`;
  };

  const renderMobileOrderCard = (order: any) => (
    <div
      key={order.orderId}
      className="v2-border v2-border-[#EAECF0] v2-rounded-[4px] v2-mb-4 v2-bg-white v2-shadow-sm v2-p-2 v2-gap-4"
    >
      <div className="v2-grid v2-grid-cols-[120px_1fr] v2-items-start v2-gap-[16px]">
        <div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-[#101828]">
            ORDER ID
          </div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-[16px] v2-text-[#101828] v2-leading-[24px]">
            {order.orderId}
          </div>
        </div>

        <div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-[#101828]">
            SUBTOTAL
          </div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-[16px] v2-text-[#101828] v2-leading-[24px]">
            ${order.orderSubTotal.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="v2-grid v2-grid-cols-[120px_1fr] v2-items-start v2-mb-4 v2-gap-[16px]">
        <div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-[#101828]">
            DATE
          </div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-[16px] v2-text-[#101828] v2-leading-[24px]">
            {new Date(order.orderDate).toLocaleDateString('en-GB')}
          </div>
        </div>

        <div>
          <div className="v2-p-[4px] v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-[#101828]">
            STATUS
          </div>
          <div className="v2-mt-[2px]">
            <span
              className={`v2-inline-flex v2-px-2 v2-rounded-[4px] ${
                statusColors[order.orderStatus] || primaryColor
              } v2-text-white v2-text-[12px] v2-font-sweet-sans-pro v2-font-normal v2-leading-[100%] v2-h-[22px] v2-gap-[10px] v2-w-full v2-justify-center v2-items-center`}
            >
              {order.orderStatus}
            </span>
          </div>
        </div>
      </div>
      <button
        onClick={() => handleOrderClick(order.orderToken)}
        className="v2-w-full v2-border v2-bg-[#FFFFFF] v2-gap-[6px] v2-border-[#154734] v2-h-[36px] v2-px-[8px] v2-flex v2-items-center v2-justify-center v2-text-[14px] v2-font-sweet-sans-pro v2-text-[#154734] v2-rounded-xs"
      >
        View Details <ChevronRightMobile />
      </button>
    </div>
  );

  return (
    <div className="v2-shadow-md v2-p-[24px] md:v2-p-[24px] sm:v2-p-4 xs:v2-p-4 v2-bg-white v2-rounded-xs">
      <div className="v2-pb-5 v2-border-b v2-border-[#E5E5E5] v2-mb-5 v2-flex v2-items-center v2-justify-between">
        <h2 className="v2-text-[#333333] v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl">Your Recent Orders</h2>
        <Button
          as="a"
          href="/orders"
          target="_blank"
          rel="noopener noreferrer"
          btnSize="sm"
          scheme="text"
          type="button"
          variant="outlined"
          className="!v2-p-0 !v2-bg-transparent v2-text-primary hover:v2-underline v2-flex v2-items-center v2-text-sm"
        >
          View all Orders
          <ChevronRight />
        </Button>
      </div>

      {isLoading ? (
        <div className="v2-flex v2-justify-center v2-items-center md:v2-h-[175px] v2-h-[175px] v2-p-8">
          <ReportsLoader />
        </div>
      ) : (
        <>
          {/* Mobile view (cards) */}
          {!isDesktop && (
            <div>
              {isError ? (
                <div className="v2-text-center v2-py-8 v2-text-red-500">Failed to load orders.</div>
              ) : !data || data?.data.length === 0 ? (
                <div className="v2-text-center v2-py-8">No recent orders found.</div>
              ) : (
                data?.data.map(order => renderMobileOrderCard(order))
              )}
            </div>
          )}

          {/* Desktop/Tablet view (table) */}
          {isDesktop && (
            <div className="custom-scroll-x-d v2-overflow-hidden xs:v2-hidden sm:v2-block">
              <table className="v2-table-fixed v2-w-full">
                <thead>
                  <tr>
                    <th className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#101828] v2-p-2 v2-text-left v2-w-[25%]">
                      ORDER ID
                    </th>
                    <th className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#101828] v2-p-2 v2-text-left v2-w-[25%]">
                      SUBTOTAL
                    </th>
                    <th className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#101828] v2-p-2 v2-text-left v2-w-[25%]">
                      DATE
                    </th>
                    <th className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#101828] v2-p-2 v2-text-left v2-w-[25%]">
                      STATUS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isError ? (
                    <tr>
                      <td colSpan={4} className="v2-text-center v2-py-8 v2-text-red-500">
                        Failed to load orders.
                      </td>
                    </tr>
                  ) : !data || data?.data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="v2-text-center v2-py-8">
                        No recent orders found.
                      </td>
                    </tr>
                  ) : (
                    data?.data.map(order => (
                      <tr
                        className="v2-cursor-pointer"
                        key={order.orderId}
                        onClick={() => handleOrderClick(order.orderToken)}
                      >
                        <td className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#101828] v2-py-[6px] v2-px-2 v2-text-left">
                          {order.orderId}
                        </td>
                        <td className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#101828] v2-py-[6px] v2-px-2 v2-text-left">
                          ${order.orderSubTotal.toFixed(2)}
                        </td>
                        <td className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#101828] v2-py-[6px] v2-px-2 v2-text-left">
                          {new Date(order.orderDate).toLocaleDateString('en-US', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </td>
                        <td className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#101828] v2-py-[6px] v2-px-2 v2-pr-0 v2-text-left">
                          <Button
                            btnSize="sm"
                            scheme="text"
                            type="button"
                            variant="outlined"
                            className="!v2-p-0 !v2-bg-transparent v2-text-primary hover:v2-underline v2-flex v2-items-center v2-gap-2 v2-text-sm"
                          >
                            <span
                              className={`v2-flex v2-px-2 v2-py-[2px] v2-rounded-xs ${
                                statusColors[order.orderStatus] || primaryColor
                              } v2-w-[100px] v2-text-white v2-text-xs v2-font-sweet-sans-pro v2-font-normal v2-justify-center`}
                            >
                              {order.orderStatus}
                            </span>
                            <ChevronRight fill="#333333" width="20px" height="20px" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecentOrders;
